import {
  clearPermissionRegistry,
  ReachableSurface,
  setPermissionRegistry,
} from '@/utils/permissionRegistry';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RESET_STORE } from './resetStore';
import type { RootState } from './store';

// Loaded inside the thunks, not at module scope: api/axios imports the
// store, and this slice is reached before the store finishes
// initialising, which put its reducer in the temporal dead zone.
const axiosInstance = async () => (await import('@/api/axios')).default;

/**
 * The caller's effective permissions, resolved server-side.
 *
 * Deliberately NOT read from localStorage the way the legacy role string is:
 * a role edit must take effect on next fetch, and a value a user can edit in
 * devtools has no business gating anything. The backend re-checks every
 * request regardless — this only decides what the UI offers.
 */

export interface PermissionsState {
  permissions: string[];
  roleId: string | null;
  /** Legacy ROLE string, still mirrored during the migration. */
  role: string | null;
  isCustomRole: boolean;
  /** Screens this user can open, served alongside the permissions. */
  surfaces: ReachableSurface[];
  status: 'idle' | 'loading' | 'ready' | 'failed';
  error: string | null;
}

const initialState: PermissionsState = {
  permissions: [],
  roleId: null,
  role: null,
  isCustomRole: false,
  surfaces: [],
  status: 'idle',
  error: null,
};

export const fetchPermissions = createAsyncThunk(
  'permissions/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const response = await (await axiosInstance()).get('/roles/me');
      if (response.data?.success) return response.data.data;
      return rejectWithValue('Failed to fetch permissions');
    } catch (error: any) {
      // Status distinguishes a transient 401 from a real failure.
      const status = error?.response?.status;
      return rejectWithValue(
        `Failed to fetch permissions${status ? ` (HTTP ${status})` : ''}`
      );
    }
  }
);

const permissionsSlice = createSlice({
  name: 'permissions',
  initialState,
  reducers: {
    clearPermissions: () => {
      clearPermissionRegistry();
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(RESET_STORE, () => {
        clearPermissionRegistry();
        return initialState;
      })
      .addCase(fetchPermissions.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        state.permissions = action.payload?.permissions ?? [];
        state.roleId = action.payload?.roleId ?? null;
        state.role = action.payload?.role ?? null;
        state.isCustomRole = !!action.payload?.isCustomRole;
        state.surfaces = action.payload?.surfaces ?? [];
        state.status = 'ready';
        state.error = null;
        // Mirror into the non-React registry that checkAccess.ts reads.
        setPermissionRegistry(
          state.permissions,
          state.role,
          state.isCustomRole,
          // Raw payload, not the store copy: these reach a module-level cache
          // and an Immer draft is revoked once the reducer returns.
          action.payload?.surfaces ?? [],
          action.payload?.allSurfacePaths ?? []
        );
      })
      .addCase(fetchPermissions.rejected, (state, action) => {
        // Fail closed: an unresolved permission set grants nothing.
        state.permissions = [];
        state.status = 'failed';
        state.error = (action.payload as string) ?? 'Failed to fetch permissions';
        clearPermissionRegistry();
      });
  },
});

export const { clearPermissions } = permissionsSlice.actions;

export const selectPermissions = (state: RootState) =>
  state.permissions.permissions;
export const selectPermissionsStatus = (state: RootState) =>
  state.permissions.status;

export default permissionsSlice.reducer;
